import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { mcpApi, type McpToken } from "@/api/mcp";
import { Button } from "@/components/ui/Button";
import { Field, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * Settings → MCP Tokens.
 *
 * Lets a user mint and revoke bearer tokens for the
 * `/api/v1/mcp` JSON-RPC endpoint. Tokens act exactly like the
 * user's session for permission purposes — admin tokens can write,
 * readonly tokens can't. The cleartext is shown ONCE on creation;
 * we never re-fetch it from the server.
 */
export function McpTokens() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  // The just-created cleartext, shown once in a banner. Cleared
  // on dismiss; navigating away also clears it (component
  // unmounts). The server never exposes the value again.
  const [freshSecret, setFreshSecret] = useState<{
    name: string;
    cleartext: string;
  } | null>(null);

  const { data: tokens, isLoading } = useQuery({
    queryKey: ["mcp", "tokens"],
    queryFn: mcpApi.list,
  });

  const createMut = useMutation({
    mutationFn: (n: string) => mcpApi.create(n),
    onSuccess: (res) => {
      setFreshSecret({ name: res.token.name, cleartext: res.cleartext });
      setName("");
      qc.invalidateQueries({ queryKey: ["mcp", "tokens"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Create failed", "error"),
  });

  const revokeMut = useMutation({
    mutationFn: (id: number) => mcpApi.revoke(id),
    onSuccess: () => {
      toast("Token revoked", "success");
      qc.invalidateQueries({ queryKey: ["mcp", "tokens"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Revoke failed", "error"),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast("Enter a name first", "error");
      return;
    }
    if (trimmed.length > 80) {
      toast("Name must be 80 characters or fewer", "error");
      return;
    }
    createMut.mutate(trimmed);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied to clipboard", "success");
    } catch {
      toast("Copy failed — select and copy manually", "error");
    }
  };

  // Split active from revoked so the table can render them
  // separately (revoked rows go to a "history" section, greyed).
  const active = (tokens ?? []).filter((t) => !t.revoked_at);
  const revoked = (tokens ?? []).filter((t) => t.revoked_at);

  return (
    <div className="space-y-6">
      <PageHeader
        title="MCP Tokens"
        description={
          "Bearer tokens for the Model Context Protocol endpoint " +
          "(/api/v1/mcp). Tokens authenticate as you — they inherit " +
          "your role and permissions. Treat them like passwords."
        }
      />

      {freshSecret && (
        <FreshSecretBanner
          name={freshSecret.name}
          cleartext={freshSecret.cleartext}
          onCopy={() => copyToClipboard(freshSecret.cleartext)}
          onDismiss={() => setFreshSecret(null)}
        />
      )}

      <form
        onSubmit={submit}
        className="space-y-3 rounded border border-line bg-surface/40 px-4 py-3"
      >
        <Field
          label="Create new token"
          hint="A short label, e.g. 'Claude Desktop on jeremy-mbp'. Helps you find it later for revocation."
        >
          <TextInput
            value={name}
            placeholder="Claude Desktop"
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
        </Field>
        <div className="flex justify-end">
          <Button type="submit" loading={createMut.isPending}>
            Create
          </Button>
        </div>
      </form>

      <section>
        <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-foreground/60">
          Active tokens ({active.length})
        </h3>
        {isLoading ? (
          <div className="text-sm text-foreground/60">Loading…</div>
        ) : active.length === 0 ? (
          <div className="rounded border border-line bg-surface/20 px-4 py-6 text-center text-sm text-foreground/60">
            No tokens yet. Create one above to start using the MCP endpoint.
          </div>
        ) : (
          <TokenTable
            tokens={active}
            onRevoke={(id) => {
              if (
                confirm(
                  "Revoke this token? Any MCP client using it will be denied access immediately.",
                )
              ) {
                revokeMut.mutate(id);
              }
            }}
          />
        )}
      </section>

      {revoked.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-foreground/40">
            Revoked ({revoked.length})
          </h3>
          <TokenTable tokens={revoked} onRevoke={null} />
        </section>
      )}

      <UsageHint />
    </div>
  );
}

function FreshSecretBanner({
  name,
  cleartext,
  onCopy,
  onDismiss,
}: {
  name: string;
  cleartext: string;
  onCopy: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded border-2 border-amber-500/60 bg-amber-500/10 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-200">
            New token created: {name}
          </div>
          <div className="mt-1 text-xs text-amber-200/80">
            This is the ONLY time the full token will be shown. Copy it now and
            store it somewhere safe — you can&apos;t retrieve it again.
          </div>
          <div className="mt-3 break-all rounded bg-black/30 px-3 py-2 font-mono text-sm text-amber-100">
            {cleartext}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button type="button" onClick={onCopy}>
            Copy
          </Button>
          <Button type="button" variant="secondary" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}

function TokenTable({
  tokens,
  onRevoke,
}: {
  tokens: McpToken[];
  onRevoke: ((id: number) => void) | null;
}) {
  return (
    <div className="overflow-x-auto rounded border border-line">
      <table className="w-full text-sm">
        <thead className="bg-surface/40 text-xs uppercase tracking-wide text-foreground/60">
          <tr>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Created</th>
            <th className="px-3 py-2 text-left">Last used</th>
            <th className="px-3 py-2 text-right" />
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => (
            <tr
              key={t.id}
              className={
                t.revoked_at
                  ? "border-t border-line text-foreground/40"
                  : "border-t border-line"
              }
            >
              <td className="px-3 py-2">{t.name}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {new Date(t.created_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 font-mono text-xs">
                {t.last_used_at ? (
                  new Date(t.last_used_at).toLocaleString()
                ) : t.revoked_at ? (
                  <span>
                    revoked {new Date(t.revoked_at).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-foreground/40">never</span>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {onRevoke && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onRevoke(t.id)}
                  >
                    Revoke
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageHint() {
  return (
    <div className="rounded border border-line bg-surface/20 px-4 py-3 text-sm">
      <div className="text-xs uppercase tracking-wide text-foreground/50">
        How to use
      </div>
      <div className="mt-2 space-y-2 text-foreground/80">
        <p>
          Add a stanza like the following to your Claude Desktop config
          (
          <code className="rounded bg-black/30 px-1 py-0.5 text-xs">
            ~/Library/Application Support/Claude/claude_desktop_config.json
          </code>
          ):
        </p>
        <pre className="overflow-x-auto rounded bg-black/30 px-3 py-2 font-mono text-xs text-foreground/90">
          {`{
  "mcpServers": {
    "arr-in-one": {
      "url": "https://arr.possiblyfaulty.com/api/v1/mcp",
      "transport": "http",
      "headers": {
        "Authorization": "Bearer arr_mcp_..."
      }
    }
  }
}`}
        </pre>
        <p className="text-xs text-foreground/60">
          The endpoint also responds to plain <code>curl</code> for testing:
          <code className="ml-1 rounded bg-black/30 px-1 py-0.5 text-xs">
            {`curl -X POST -H "Authorization: Bearer $TOKEN" -H "Content-Type: application/json" -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}' https://arr.possiblyfaulty.com/api/v1/mcp`}
          </code>
        </p>
      </div>
    </div>
  );
}
